"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, CircleCheck as CheckCircle2, Cpu, Gauge, Layers, ShieldAlert, SlidersHorizontal, Wind } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
export default function ElectricBull() {
  const shouldReduceMotion = useReducedMotion();
  const criteriaList = [{
    label: "Operator Controller",
    desc: "Dual manual joysticks, presets, and stop response.",
    icon: SlidersHorizontal
  }, {
    label: "Published Power Demands",
    desc: "Dedicated line and operating draw specifications.",
    icon: Gauge
  }, {
    label: "Safety Functions",
    desc: "Emergency cutoff switches and auto-stop sensors.",
    icon: ShieldAlert
  }, {
    label: "Operating Footprint",
    desc: "Clearance radius for base frame and rider dismount.",
    icon: Layers
  }, {
    label: "Included Equipment",
    desc: "Base mount, motor chassis, cabling, and hardware.",
    icon: Cpu
  }];
  return <section id="choosingelectricbull" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 md:px-6 max-w-5xl">
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 12
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1]
      }} className="relative bg-card border border-border p-6 md:p-10 shadow-sm">
          {/* Top Architectural Spec Plate Header Bar */}
          <div className="flex flex-wrap items-center justify-between gap-4 pb-6 mb-6 border-b border-border">
            <div className="flex items-center gap-3">
              <span className="inline-block w-2.5 h-2.5 bg-primary" />
              <p className="font-mono text-xs tracking-wider uppercase text-muted-foreground font-semibold">
                EQUIPMENT SELECTION GUIDE // REF-EB700
              </p>
            </div>
            <div className="font-mono text-xs text-muted-foreground border border-border px-2.5 py-1 bg-muted">
              COMMERCIAL SPECIFICATION
            </div>
          </div>

          {/* Heading and Narrative */}
          <div className="space-y-4 mb-8">
            <h2 className="font-serif text-3xl md:text-5xl font-bold tracking-tight uppercase text-foreground">
              Choosing an Electric Mechanical Bull
            </h2>
            <p className="text-base md:text-lg text-foreground/90 leading-relaxed max-w-3xl">
              An electric mechanical bull uses powered motion and an operator control system, but different models can use different configurations. Therefore, buyers should compare the exact machine rather than assume every electric bull has the same controller, movements or electrical requirements.
            </p>
          </div>

          {/* Evaluation Criteria Spec Block */}
          <div className="bg-muted border border-border p-5 md:p-6 mb-8">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle2 className="size-5 text-primary" />
              <h3 className="font-mono text-xs md:text-sm uppercase font-bold tracking-wider text-foreground">
                Critical Inspection Checklist
              </h3>
            </div>
            <p className="text-sm md:text-base text-foreground/90 leading-normal mb-5">
              When looking at an electric bull for sale, review the controller, published power requirements, safety functions, operating footprint and equipment included with the package.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {criteriaList.map((item, i) => {
              const Icon = item.icon;
              return <div key={item.label} data-index={i} className="bg-card border border-border p-3.5 flex flex-col justify-between">
                    <div className="flex items-center gap-2 mb-1.5">
                      <Icon className="size-4 text-primary shrink-0" />
                      <span className="font-mono text-xs font-semibold uppercase text-foreground">
                        {item.label}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {item.desc}
                    </p>
                  </div>;
            })}
            </div>
          </div>

          {/* Technical Inflatable & Blower Accessory Note */}
          <Card className="bg-background border-l-4 border-l-primary border-y-border border-r-border mb-8 shadow-none">
            <CardContent className="p-4 md:p-5 flex items-start gap-4">
              <div className="bg-muted p-2 border border-border shrink-0 mt-0.5">
                <Wind className="size-5 text-primary" />
              </div>
              <div className="space-y-1">
                <span className="font-mono text-xs font-bold uppercase tracking-wider text-muted-foreground block">
                  Accessory Verification Note
                </span>
                <p className="text-sm md:text-base text-foreground/90 leading-relaxed">
                  If the listing includes an inflatable safety arena, also confirm whether the blower comes with it and what setup information is available.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Bottom Action Area */}
          <div className="pt-4 border-t border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="font-mono text-xs text-muted-foreground">
              Direct commercial sales. Nationwide crating and freight dispatched from inventory.
            </div>
            <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 font-mono text-xs uppercase tracking-wider h-11 px-6 font-semibold">
              <Link to="#" className="inline-flex items-center gap-2">
                <span>View Electric Bulls for Sale</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}